import { useState, useEffect, useCallback } from "react";
import { apiClient } from "@/lib/api-client.js";

interface SystemStatus {
  version: string;
  environment: string;
  authMode: string;
  uptimeSeconds: number;
  nodeVersion: string;
  timestamp: string;
  database: { status: "ok" | "error"; latencyMs: number; error?: string };
  ai: { active: boolean; provider: string; model: string | null; mode: string };
  knowledgeBase: { components: number; foerderungen: number; foerderStand: string };
  backgroundJobs: { status: string; note: string };
  memory: { rssMb: number; heapUsedMb: number; heapTotalMb: number };
  counts: {
    projects: number; users: number; customers: number;
    components: number; auditEntries: number; configuredSettings: number;
  };
}

function Dot({ ok }: { ok: boolean }) {
  return <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: ok ? "var(--color-success)" : "var(--color-error)", marginRight: 8 }} />;
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "var(--color-primary)" }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function fmtUptime(s: number): string {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

export function SystemstatusSection() {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    void apiClient
      .get<SystemStatus>("/admin/system-status")
      .then((res) => {
        if (res.success) setStatus(res.data);
        else setError(res.error.message);
      })
      .catch(() => setError("Status konnte nicht geladen werden."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  if (loading) return <div style={{ color: "var(--color-text-muted)", fontSize: 13 }}>Lade Systemstatus…</div>;
  if (error || !status) return <div style={{ color: "var(--color-error)", fontSize: 13 }}>{error ?? "Kein Status."}</div>;

  const dbOk = status.database.status === "ok";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700 }}>Systemstatus</h2>
        <button className="btn-secondary" onClick={load} style={{ fontSize: 13 }}>↻ Aktualisieren</button>
      </div>

      {/* Subsystem-Status */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}><Dot ok={dbOk} />Datenbank</div>
            <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
              {dbOk ? `Erreichbar · ${status.database.latencyMs} ms` : `Fehler: ${status.database.error ?? "unbekannt"}`}
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}><Dot ok={status.ai.active} />KI-Subsystem</div>
            <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
              {status.ai.mode === "rule_based" ? "Regelbasiert" : "LLM konfiguriert"} · {status.ai.provider}
              {status.ai.model ? ` · ${status.ai.model}` : ""}
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}><Dot ok={true} />Knowledge Base</div>
            <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
              {status.knowledgeBase.components} Komponenten · {status.knowledgeBase.foerderungen} Förderungen
              <br />Stand {status.knowledgeBase.foerderStand}
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}><Dot ok={false} />Hintergrundjobs</div>
            <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>{status.backgroundJobs.note}</div>
          </div>
        </div>
      </div>

      {/* Kennzahlen */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14, marginBottom: 20 }}>
        <StatCard label="Projekte" value={String(status.counts.projects)} />
        <StatCard label="Benutzer" value={String(status.counts.users)} />
        <StatCard label="Kunden" value={String(status.counts.customers)} />
        <StatCard label="Komponenten" value={String(status.counts.components)} />
        <StatCard label="Audit-Einträge" value={String(status.counts.auditEntries)} />
        <StatCard label="Gespeicherte Settings" value={String(status.counts.configuredSettings)} sub="Schlüssel" />
      </div>

      {/* Laufzeit / Ressourcen */}
      <div className="card">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14, fontSize: 13 }}>
          <div><span style={{ color: "var(--color-text-muted)" }}>Version</span><br /><strong>{status.version}</strong></div>
          <div><span style={{ color: "var(--color-text-muted)" }}>Umgebung</span><br /><strong>{status.environment}</strong></div>
          <div><span style={{ color: "var(--color-text-muted)" }}>Auth-Modus</span><br /><strong>{status.authMode}</strong></div>
          <div><span style={{ color: "var(--color-text-muted)" }}>Laufzeit</span><br /><strong>{fmtUptime(status.uptimeSeconds)}</strong></div>
          <div><span style={{ color: "var(--color-text-muted)" }}>Node</span><br /><strong>{status.nodeVersion}</strong></div>
          <div><span style={{ color: "var(--color-text-muted)" }}>Speicher (RSS)</span><br /><strong>{status.memory.rssMb} MB</strong></div>
          <div><span style={{ color: "var(--color-text-muted)" }}>Heap</span><br /><strong>{status.memory.heapUsedMb} / {status.memory.heapTotalMb} MB</strong></div>
          <div><span style={{ color: "var(--color-text-muted)" }}>Stand</span><br /><strong>{new Date(status.timestamp).toLocaleTimeString("de-AT")}</strong></div>
        </div>
      </div>
    </div>
  );
}
